// ./src/components/features/UpcomingServicesList/UpcomingServicesList.tsx
import React, { useCallback, useMemo } from "react";
import {
  Stack,
  Text,
  Box,
  Group,
  Card,
  Badge,
  Skeleton,
  Paper,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { SERVICE_INTERVAL_TYPES } from "@/config/serviceIntervalTypes";

import { getCarDisplayName } from "@/utils/carHelpers";
import { formatDateTime } from "@/utils/dateHelpers";

import { ServiceIntervalNext, UrgencyStatus } from "@/utils/api/ServiceIntervalNextService";

import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { useAppEssentialsContext } from "@/contexts/AppEssentialsContext";

import { AppButton } from "@/components/generic/AppButton";
import { AppIcon } from "@/components/generic/AppIcon";
import { AppTable, AppTableColumn } from "@/components/generic/AppTable";
import { EmptyState } from "@/components/generic/EmptyState";

// ============================================
// Types
// ============================================

export interface UpcomingServicesListProps {
  /** List of upcoming services to display */
  services: ServiceIntervalNext[];

  /** Whether data is loading */
  loading?: boolean;

  /** Callback when edit action is clicked */
  onEditService?: (service: ServiceIntervalNext) => void;

  /** Callback when add expense is clicked */
  onAddExpense?: (carId: string, kindId: number) => void;

  /** ID of service currently being updated */
  updatingId?: string | null;

  /** ID of recently edited service to highlight */
  highlightedServiceId?: string | null;
}

// ============================================
// Constants
// ============================================

const URGENCY_COLORS: Record<UrgencyStatus, string> = {
  overdue: "red",
  due_soon: "orange",
  upcoming: "blue",
  ok: "gray",
};

const URGENCY_ICONS: Record<UrgencyStatus, string> = {
  overdue: "AlertTriangle",
  due_soon: "Clock",
  upcoming: "Calendar",
  ok: "Check",
};

// ============================================
// Helper Functions
// ============================================

/**
 * Format mileage value with unit
 */
const formatMileage = (
  value: number | undefined | null,
  unit: string
): string => {
  if (value == null) return "—";
  return `${value.toLocaleString()} ${unit}`;
};

/**
 * Get car name from service record using shared helper
 */
const getServiceCarName = (car: ServiceIntervalNext["car"]): string => {
  if (!car) return "Vehicle";
  // Use shared helper - cast to any since the types are compatible
  return getCarDisplayName(car as any) || "Vehicle";
};

// ============================================
// Service Card Component (Mobile)
// ============================================

interface ServiceCardProps {
  service: ServiceIntervalNext;
  distanceUnit: string;
  onEdit?: (service: ServiceIntervalNext) => void;
  onAddExpense?: (carId: string, kindId: number) => void;
  isUpdating: boolean;
  highlighted: boolean;
  getServiceName: (service: ServiceIntervalNext) => string;
}

const ServiceCardComponent: React.FC<ServiceCardProps> = ({
  service,
  distanceUnit,
  onEdit,
  onAddExpense,
  isUpdating,
  highlighted,
  getServiceName,
}) => {
  const { t } = useTranslation(["services", "common"]);

  const urgencyColor = URGENCY_COLORS[service.urgencyStatus || "ok"];
  const urgencyIcon = URGENCY_ICONS[service.urgencyStatus || "ok"];

  const showDate =
    service.intervalType === SERVICE_INTERVAL_TYPES.DAYS ||
    service.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_OR_DAYS ||
    service.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_AND_DAYS;

  const showMileage =
    service.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE ||
    service.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_OR_DAYS ||
    service.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_AND_DAYS;

  const handleEdit = useCallback(() => {
    onEdit?.(service);
  }, [onEdit, service]);

  const handleAddExpense = useCallback(() => {
    if (service.carId && service.kindId) {
      onAddExpense?.(service.carId, service.kindId);
    }
  }, [onAddExpense, service.carId, service.kindId]);

  // Format remaining text
  const getRemainingText = () => {
    const parts: string[] = [];

    if (showDate && service.remainingDays != null) {
      if (service.remainingDays < 0) {
        parts.push(
          t("services:card.daysOverdue", {
            count: Math.abs(service.remainingDays),
          })
        );
      } else {
        parts.push(
          t("services:card.daysRemaining", { count: service.remainingDays })
        );
      }
    }

    if (showMileage && service.remainingMileage != null) {
      if (service.remainingMileage < 0) {
        parts.push(
          t("services:card.mileageOverdue", {
            value: Math.abs(service.remainingMileage).toLocaleString(),
            unit: distanceUnit,
          })
        );
      } else {
        parts.push(
          t("services:card.mileageRemaining", {
            value: service.remainingMileage.toLocaleString(),
            unit: distanceUnit,
          })
        );
      }
    }

    return parts.join(" / ");
  };

  return (
    <Card
      withBorder
      padding="sm"
      radius="sm"
      bg={highlighted ? "green.0" : undefined}
      opacity={isUpdating ? 0.5 : 1}
      style={{ transition: "opacity 150ms ease" }}
    >
      <Stack gap="sm">
        {/* Header with service name and urgency badge */}
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Text fw={600} size="md">
              {getServiceName(service)}
            </Text>
            <Text size="sm" c="dimmed">
              {getServiceCarName(service.car)}
            </Text>
          </Stack>
          <Badge
            color={urgencyColor}
            variant="light"
            leftSection={<AppIcon iconName={urgencyIcon} size={12} />}
          >
            {t(`services:status.${service.urgencyStatus || "ok"}`)}
          </Badge>
        </Group>

        {/* Due info */}
        <Box>
          <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
            {t("services:card.nextDue")}
          </Text>
          <Group gap="md">
            {showDate && (
              <Text size="sm">
                {formatDateTime(service.nextWhenDo, "medium")}
              </Text>
            )}
            {showMileage && (
              <Text size="sm">
                {formatMileage(service.nextOdometer, distanceUnit)}
              </Text>
            )}
          </Group>
        </Box>

        {/* Remaining */}
        {(service.remainingDays != null ||
          service.remainingMileage != null) && (
            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                {t("services:card.remaining")}
              </Text>
              <Text size="sm" c={urgencyColor} fw={500}>
                {getRemainingText()}
              </Text>
            </Box>
          )}

        {/* Last service info */}
        {(service.maxWhenDone || service.maxOdometer) && (
          <Box>
            <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
              {t("services:card.lastService")}
            </Text>
            <Group gap="md">
              {service.maxWhenDone && (
                <Text size="sm" c="dimmed">
                  {formatDateTime(service.maxWhenDone, "medium")}
                </Text>
              )}
              {service.maxOdometer && (
                <Text size="sm" c="dimmed">
                  {formatMileage(service.maxOdometer, distanceUnit)}
                </Text>
              )}
            </Group>
          </Box>
        )}

        {/* Action buttons */}
        <Group justify="flex-end" gap="xs" mt="xs">
          {onEdit && (
            <AppButton
              variant="secondary"
              size="xs"
              iconName="Edit"
              onClick={handleEdit}
              disabled={isUpdating}
            >
              {t("services:card.editSchedule")}
            </AppButton>
          )}
          {onAddExpense && (
            <AppButton
              size="xs"
              iconName="Plus"
              onClick={handleAddExpense}
              disabled={isUpdating}
            >
              {t("services:card.addExpense")}
            </AppButton>
          )}
        </Group>
      </Stack>
    </Card>
  );
};

const ServiceCard = React.memo(ServiceCardComponent);

// ============================================
// Loading Skeleton
// ============================================

/** Mobile card skeleton */
const CardSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => {
  return (
    <Stack gap="sm">
      {Array.from({ length: count }).map((_, index) => (
        <Card key={index} withBorder padding="sm" radius="sm">
          <Stack gap="sm">
            <Group justify="space-between">
              <Stack gap={4}>
                <Skeleton height={20} width={150} />
                <Skeleton height={16} width={100} />
              </Stack>
              <Skeleton height={24} width={80} radius="xl" />
            </Group>
            <Stack gap={4}>
              <Skeleton height={12} width={70} />
              <Skeleton height={16} width={120} />
            </Stack>
            <Stack gap={4}>
              <Skeleton height={12} width={80} />
              <Skeleton height={16} width={140} />
            </Stack>
            <Group justify="flex-end" gap="xs">
              <Skeleton height={32} width={110} radius="sm" />
              <Skeleton height={32} width={110} radius="sm" />
            </Group>
          </Stack>
        </Card>
      ))}
    </Stack>
  );
};

/** Desktop table skeleton */
const TableSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => {
  return (
    <Paper withBorder radius="sm">
      {/* Table header */}
      <Group
        px="md"
        py="sm"
        gap="md"
        wrap="nowrap"
        style={{ borderBottom: "1px solid var(--mantine-color-gray-2)" }}
        bg="gray.0"
      >
        <Box style={{ flex: 1, minWidth: 200 }}>
          <Skeleton height={14} width={60} />
        </Box>
        <Box w={180}>
          <Skeleton height={14} width={70} />
        </Box>
        <Box w={150}>
          <Skeleton height={14} width={80} />
        </Box>
        <Box w={150}>
          <Skeleton height={14} width={90} />
        </Box>
        <Box w={120} />
      </Group>

      {/* Table rows */}
      {Array.from({ length: count }).map((_, index) => (
        <Group
          key={index}
          px="md"
          py="sm"
          gap="md"
          wrap="nowrap"
          style={{
            borderBottom:
              index < count - 1
                ? "1px solid var(--mantine-color-gray-2)"
                : undefined,
          }}
        >
          {/* Service column */}
          <Box style={{ flex: 1, minWidth: 200 }}>
            <Stack gap={4}>
              <Group gap="xs">
                <Skeleton height={16} width={140} />
                <Skeleton height={20} width={70} radius="xl" />
              </Group>
              <Skeleton height={14} width={90} />
            </Stack>
          </Box>

          {/* Next Due column */}
          <Box w={180}>
            <Stack gap={2}>
              <Skeleton height={14} width={100} />
              <Skeleton height={14} width={80} />
            </Stack>
          </Box>

          {/* Remaining column */}
          <Box w={150}>
            <Stack gap={2}>
              <Skeleton height={14} width={110} />
              <Skeleton height={14} width={100} />
            </Stack>
          </Box>

          {/* Last Service column */}
          <Box w={150}>
            <Stack gap={2}>
              <Skeleton height={14} width={90} />
              <Skeleton height={14} width={70} />
            </Stack>
          </Box>

          {/* Actions column */}
          <Group w={120} gap="xs" justify="flex-end" wrap="nowrap">
            <Skeleton height={32} width={32} radius="sm" />
            <Skeleton height={32} width={32} radius="sm" />
          </Group>
        </Group>
      ))}
    </Paper>
  );
};

/** Responsive loading skeleton */
const LoadingSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => {
  return (
    <>
      {/* Mobile: Card skeletons */}
      <Box hiddenFrom="md">
        <CardSkeleton count={count} />
      </Box>

      {/* Desktop: Table skeleton */}
      <Box visibleFrom="md">
        <TableSkeleton count={count} />
      </Box>
    </>
  );
};

// ============================================
// Desktop Table Component
// ============================================

interface ServicesTableProps {
  services: ServiceIntervalNext[];
  distanceUnit: string;
  onEdit?: (service: ServiceIntervalNext) => void;
  onAddExpense?: (carId: string, kindId: number) => void;
  updatingId: string | null;
  highlightedServiceId: string | null;
  getServiceName: (service: ServiceIntervalNext) => string;
}

const ServicesTable: React.FC<ServicesTableProps> = ({
  services,
  distanceUnit,
  onEdit,
  onAddExpense,
  updatingId,
  highlightedServiceId,
  getServiceName,
}) => {
  const { t } = useTranslation(["services", "common"]);

  const columns: AppTableColumn<ServiceIntervalNext>[] = useMemo(
    () => [
      {
        key: "service",
        label: t("services:table.service"),
        minWidth: 200,
        flex: true,
        render: (_value, record) => {
          const urgencyColor = URGENCY_COLORS[record.urgencyStatus || "ok"];
          const urgencyIcon = URGENCY_ICONS[record.urgencyStatus || "ok"];

          return (
            <Stack gap={4}>
              <Group gap="xs">
                <Text fw={500}>{getServiceName(record)}</Text>
                <Badge
                  color={urgencyColor}
                  variant="light"
                  size="sm"
                  leftSection={<AppIcon iconName={urgencyIcon} size={10} />}
                >
                  {t(`services:status.${record.urgencyStatus || "ok"}`)}
                </Badge>
              </Group>
              <Text size="sm" c="dimmed">
                {getServiceCarName(record.car)}
              </Text>
            </Stack>
          );
        },
      },
      {
        key: "nextDue",
        label: t("services:table.nextDue"),
        width: 180,
        render: (_value, record) => {
          const showDate =
            record.intervalType === SERVICE_INTERVAL_TYPES.DAYS ||
            record.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_OR_DAYS ||
            record.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_AND_DAYS;

          const showMileage =
            record.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE ||
            record.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_OR_DAYS ||
            record.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_AND_DAYS;

          return (
            <Stack gap={2}>
              {showDate && (
                <Text size="sm">
                  {formatDateTime(record.nextWhenDo, "medium")}
                </Text>
              )}
              {showMileage && (
                <Text size="sm">
                  {formatMileage(record.nextOdometer, distanceUnit)}
                </Text>
              )}
            </Stack>
          );
        },
      },
      {
        key: "remaining",
        label: t("services:table.remaining"),
        width: 150,
        render: (_value, record) => {
          const urgencyColor = URGENCY_COLORS[record.urgencyStatus || "ok"];

          const showDate =
            record.intervalType === SERVICE_INTERVAL_TYPES.DAYS ||
            record.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_OR_DAYS ||
            record.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_AND_DAYS;

          const showMileage =
            record.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE ||
            record.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_OR_DAYS ||
            record.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_AND_DAYS;

          return (
            <Stack gap={2}>
              {showDate && record.remainingDays != null && (
                <Text size="sm" c={urgencyColor} fw={500}>
                  {record.remainingDays < 0
                    ? t("services:card.daysOverdue", {
                      count: Math.abs(record.remainingDays),
                    })
                    : t("services:card.daysRemaining", {
                      count: record.remainingDays,
                    })}
                </Text>
              )}
              {showMileage && record.remainingMileage != null && (
                <Text size="sm" c={urgencyColor} fw={500}>
                  {record.remainingMileage < 0
                    ? t("services:card.mileageOverdue", {
                      value: Math.abs(
                        record.remainingMileage
                      ).toLocaleString(),
                      unit: distanceUnit,
                    })
                    : t("services:card.mileageRemaining", {
                      value: record.remainingMileage.toLocaleString(),
                      unit: distanceUnit,
                    })}
                </Text>
              )}
            </Stack>
          );
        },
      },
      {
        key: "lastService",
        label: t("services:table.lastService"),
        width: 150,
        render: (_value, record) => {
          return (
            <Stack gap={2}>
              {record.maxWhenDone && (
                <Text size="sm" c="dimmed">
                  {formatDateTime(record.maxWhenDone, "medium")}
                </Text>
              )}
              {record.maxOdometer && (
                <Text size="sm" c="dimmed">
                  {formatMileage(record.maxOdometer, distanceUnit)}
                </Text>
              )}
            </Stack>
          );
        },
      },
      {
        key: "actions",
        label: "",
        width: 120,
        align: "right" as const,
        render: (_value, record) => {
          const isThisUpdating = updatingId === record.id;

          const handleEdit = () => onEdit?.(record);
          const handleAddExpense = () => {
            if (record.carId && record.kindId) {
              onAddExpense?.(record.carId, record.kindId);
            }
          };

          return (
            <Group gap="xs" wrap="nowrap" justify="flex-end">
              {onEdit && (
                <AppButton
                  variant="secondary"
                  size="sm"
                  iconName="Edit"
                  onClick={handleEdit}
                  disabled={isThisUpdating}
                  tooltip={t("services:card.editSchedule")}
                />
              )}
              {onAddExpense && (
                <AppButton
                  size="sm"
                  iconName="Plus"
                  onClick={handleAddExpense}
                  disabled={isThisUpdating}
                  tooltip={t("services:card.addExpense")}
                />
              )}
            </Group>
          );
        },
      },
    ],
    [distanceUnit, updatingId, onEdit, onAddExpense, getServiceName, t]
  );

  return (
    <AppTable
      data={services}
      columns={columns}
      getRowKey={(record) => record.id || String(record.kindId)}
      highlightRow={(record) => record?.id === highlightedServiceId}
      withBorder
      highlightOnHover
      size="sm"
    />
  );
};

// ============================================
// Main Component
// ============================================

const UpcomingServicesListComponent: React.FC<UpcomingServicesListProps> = ({
  services,
  loading = false,
  onEditService,
  onAddExpense,
  updatingId = null,
  highlightedServiceId = null,
}) => {
  const { t } = useTranslation(["services", "common"]);
  const { userProfile } = useUserProfileContext();
  const { getExpenseKindNameById } = useAppEssentialsContext();

  // Get distance unit from user profile
  const distanceUnit = userProfile?.distanceIn || "km";

  // Helper to get service name
  const getServiceName = useCallback(
    (service: ServiceIntervalNext): string => {
      // First try from the service's embedded expenseKind
      if (service.expenseKind?.name) {
        return service.expenseKind.name;
      }

      // Fallback to lookup from AppEssentials context
      if (service.kindId != null) {
        const name = getExpenseKindNameById(service.kindId);
        if (name) {
          return name;
        }
      }

      // Last resort fallback
      return t("services:unknownService");
    },
    [getExpenseKindNameById, t]
  );

  // Loading state
  if (loading && services.length === 0) {
    return <LoadingSkeleton count={3} />;
  }

  // Empty state
  if (!loading && services.length === 0) {
    return (
      <Paper withBorder p="xl">
        <EmptyState
          title={t("services:emptyState.title")}
          description={t("services:emptyState.description")}
          iconName="CalendarCheck"
          size="md"
        />
      </Paper>
    );
  }

  return (
    <>
      {/* Mobile: Card list */}
      <Box hiddenFrom="md">
        <Stack gap="md">
          {services.map((service) => (
            <ServiceCard
              key={service.id || `${service.carId}-${service.kindId}`}
              service={service}
              distanceUnit={distanceUnit}
              onEdit={onEditService}
              onAddExpense={onAddExpense}
              isUpdating={updatingId === service.id}
              highlighted={service.id === highlightedServiceId}
              getServiceName={getServiceName}
            />
          ))}
        </Stack>
      </Box>

      {/* Desktop: Table */}
      <Box visibleFrom="md">
        <ServicesTable
          services={services}
          distanceUnit={distanceUnit}
          onEdit={onEditService}
          onAddExpense={onAddExpense}
          updatingId={updatingId}
          highlightedServiceId={highlightedServiceId}
          getServiceName={getServiceName}
        />
      </Box>
    </>
  );
};

export const UpcomingServicesList = React.memo(UpcomingServicesListComponent);

export default UpcomingServicesList;
